import React, { useState, useRef, useEffect } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import OutsideClickHandler from '../OutsideClickHandler/OutsideClickHandler';
import ValidationError from '../ValidationError/ValidationError';

import css from './FieldCustomCountryPicker.module.css';

/**
 * Custom country select with a scrollable panel (native <select> cannot honor max-height).
 *
 * @component
 */
const CustomCountryPickerComponent = props => {
  const {
    id,
    label,
    labelClassName,
    className,
    disabled,
    input,
    meta,
    options = [],
    placeholder,
    maxHeight = 240,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  const selectedValue = input.value;
  const selectedOption = options.find(
    option =>
      String(option.value).toLowerCase() === String(selectedValue).toLowerCase() ||
      String(option.label).toLowerCase() === String(selectedValue).toLowerCase()
  );

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }, [isOpen]);

  const handleSelect = value => {
    input.onChange(value);
    setIsOpen(false);
  };

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  const displayLabel = selectedOption ? selectedOption.label : placeholder;

  return (
    <OutsideClickHandler
      rootClassName={css.outsideHandler}
      className={classNames(css.root, className)}
      onOutsideClick={() => setIsOpen(false)}
    >
      {label ? (
        <label htmlFor={id} className={labelClassName}>
          {label}
        </label>
      ) : null}
      <div className={css.wrapper}>
        <button
          type="button"
          id={id}
          disabled={disabled}
          className={classNames(css.trigger, {
            [css.triggerPlaceholder]: !selectedOption,
            [css.triggerDisabled]: disabled,
          })}
          onClick={() => !disabled && setIsOpen(prev => !prev)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        >
          <span>{displayLabel}</span>
        </button>
        {isOpen ? (
          <div className={css.dropdown} style={{ maxHeight }}>
            <div className={css.searchWrapper}>
              <svg
                className={css.searchIcon}
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="rgba(255, 255, 255, 0.6)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                className={css.searchInput}
                placeholder="Search country..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
              />
              {searchQuery ? (
                <button
                  type="button"
                  className={css.searchClear}
                  onClick={e => {
                    e.stopPropagation();
                    setSearchQuery('');
                    if (searchInputRef.current) {
                      searchInputRef.current.focus();
                    }
                  }}
                >
                  &times;
                </button>
              ) : null}
            </div>
            <ul className={css.optionsList} role="listbox">
              {filteredOptions.length > 0 ? (
                filteredOptions.map(option => {
                  const isSelected = String(option.value) === String(selectedValue);
                  return (
                    <li key={option.value} role="presentation">
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={classNames(css.option, {
                          [css.optionActive]: isSelected,
                        })}
                        onClick={() => handleSelect(option.value)}
                      >
                        <span>{option.label}</span>
                      </button>
                    </li>
                  );
                })
              ) : (
                <li className={css.noResults}>No countries found</li>
              )}
            </ul>
          </div>
        ) : null}
      </div>
      <ValidationError fieldMeta={meta} />
    </OutsideClickHandler>
  );
};

/**
 * Final Form field for the custom country select dropdown.
 *
 * @component
 */
const FieldCustomCountryPicker = props => {
  return <Field component={CustomCountryPickerComponent} {...props} />;
};

export default FieldCustomCountryPicker;
